import { type ComponentPropsWithoutRef, forwardRef } from "react"

/**
 * Creates a utility Flex component based on https://chakra-ui.com/docs/components/flex
 *
 * @todo: Make this generic: https://stackoverflow.com/a/66568474/3554402
 */
export type FlexProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Equivalent to flex-direction.
   *
   * @default column
   */
  direction?: "column" | "row"

  /**
   * Equivalent to align-items.
   *
   * @default center
   */
  align?:
    | "initial"
    | "center"
    | "flex-end"
    | "flex-start"
    | "start"
    | "stretch"
    | "baseline"

  /**
   * Equivalent to justfy-content.
   *
   * @default initial
   */
  justify?:
    | "initial"
    | "center"
    | "flex-end"
    | "flex-start"
    | "space-around"
    | "space-between"
    | "space-evenly"

  /**
   * Equivalent to gap.
   *
   * @default var(--space-sm-6)
   */
  gap?: string

  /**
   * Equivalent to flex-wrap.
   *
   * @default nowrap
   */
  wrap?: "nowrap" | "wrap" | "wrap-reverse"
}
export const Flex = forwardRef<HTMLDivElement, FlexProps>(
  ({ style, direction, align, justify, gap, wrap, ...props }, ref) => {
    return (
      <div
        ref={ref}
        {...props}
        style={{
          ...style,
          display: "flex",
          flexDirection: direction ?? "column",
          alignItems: align ?? "center",
          justifyContent: justify ?? "initial",
          gap: gap ?? "var(--gap-sm)",
          flexWrap: wrap ?? "nowrap",
        }}
      />
    )
  },
)

Flex.displayName = "Flex"

/**
 * A utility component to fill empty space in a flex container.
 */
export type FlexSpaceProps = ComponentPropsWithoutRef<"div"> & {
  className?: string
}

export const FlexSpace = forwardRef<HTMLDivElement, FlexSpaceProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} {...props} className={`flex-space ${className ?? ""}`} />
  ),
)

FlexSpace.displayName = "FlexSpace"
